import React, { useCallback, useEffect, useState } from 'react'
import axios, { AxiosResponse } from 'axios'
import { isChrome, isEdge, isFirefox, isIOS, isOpera } from 'react-device-detect'
import { useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import _debounce from 'lodash/debounce'
import { Dialog, Transition } from '@headlessui/react'
import Image from 'next/legacy/image'

import { Button, Dropdown, Input, Text } from 'components/atomic'
import ModalCloseIcon from 'components/icon/modal-close'

import { founderBlurDataURL } from 'constants/blurDataURL'
import { parseProductName } from 'utils/utils'
import { InventoryItem } from '..'
import { dkProductBundles, PaidProductRarityColor } from 'lib/constants'
import { LoadingSpinner } from 'components/delayed'
import TransferConfirmModal from '../transferConfirmModal'

const formSchema = yup.object().shape({
  email: yup.string().email('Email must be a valid email').required('Email is required'),
})
const defaultValues = {
  email: '',
}

interface InventoryItemOpenModalProps {
  show: boolean
  item: InventoryItem
  onOpened: (product: any) => void
  onCloseModal: () => void
  dkCratesCount: number
  refetchInventoryData: () => void
}

const InventoryItemOpenModal: React.FC<InventoryItemOpenModalProps> = ({
  show,
  item,
  onOpened,
  onCloseModal,
  dkCratesCount,
  refetchInventoryData,
}: InventoryItemOpenModalProps) => {
  const { isDkCrate, isDayZeroPatch, productName } = item
  const quantityOptions = Array.from({ length: dkCratesCount }, (_, i) => ({
    label: `${i + 1}`,
    value: `${i + 1}`,
  }))

  const [product, setProduct] = useState(
    isDayZeroPatch
      ? {
          rarity: 'Legendary',
          name: 'Day Zero Claw Patch',
          category: '',
          image: '/images/profile/clawnft_animation10.gif',
        }
      : {
          ...parseProductName(productName),
          image: `/images/profile/paid-products/${productName}.png`,
        },
  )
  const [isOpening, setIsOpening] = useState(false)
  const [isCrateOpened, setIsCrateOpened] = useState(false)
  const [imageError, setImageError] = useState(false)

  const {
    register,
    control,
    formState: { errors, isSubmitting, isValid },
  } = useForm({ defaultValues, resolver: yupResolver(formSchema) })
  const formValues = useWatch({ control })

  const [isVerifyUserEmailLoading, setIsVerifyUserEmailLoading] = useState(false)
  const [verifyUserEmailStatus, setVerifyUserEmailStatus] = useState<'success' | 'error' | null>(null)
  const [receiverUserId, setReceiverUserId] = useState<string>('')
  const [isTransferConfirmModalOpen, setIsTransferConfirmModalOpen] = useState(false)
  const [quantity, setQuantity] = useState<string>('Quantity')

  const handleDebounceFn = async (email) => {
    try {
      setIsVerifyUserEmailLoading(true)
      const response = await axios.get(`/api/verify-user-email?email=${encodeURIComponent(email)}`)
      if (response.data.emailUserId) {
        setVerifyUserEmailStatus('success')
        setReceiverUserId(response.data.emailUserId)
      } else {
        setVerifyUserEmailStatus('error')
      }
    } catch (error) {
      if (error.response) {
        const message = error.response.data.error
      } else {
      }
      setVerifyUserEmailStatus('error')
    } finally {
      setIsVerifyUserEmailLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(_debounce(handleDebounceFn, 1000), [])

  useEffect(() => {
    setVerifyUserEmailStatus(null)
    if (formValues.email && isValid) {
      debounceFn(formValues.email)
    }
  }, [isValid, formValues, debounceFn])

  const handleSelectQuantity = (value) => {
    setQuantity(value.value)
  }

  const handleTransferClick = () => {
    setIsTransferConfirmModalOpen(true)
  }

  const handleCloseTransferConfirmModal = () => {
    setIsTransferConfirmModalOpen(false)
  }

  const handleTransferSuccess = () => {
    refetchInventoryData()
    setQuantity('Quantity')
  }

  const onClickOpenCrate = async () => {
    setIsOpening(true)
    try {
      const res: AxiosResponse<any, any> = await axios.post('/api/dk-crate-open', { validateStatus: (status) => true })
      if (res.status !== 200) {
        console.log(`DK Crate Open error : ${res.data.error}`)
        alert(res.data.error)
        return
      }

      const droppedItemName = res.data.droppedItemName
      setProduct({
        ...parseProductName(droppedItemName),
        image: `/images/profile/paid-products/${droppedItemName}.png`,
      })
      onOpened({
        productName: droppedItemName,
        isDkCrate: false,
        isDayZeroPatch: false,
      })
    } catch (error) {
      console.log(`DK Crate Open error : ${error}`)
      alert(error)
    }
  }

  const onOpeningVideoEnded = () => {
    setIsOpening(false)
    setIsCrateOpened(true)
  }

  return (
    <>
      <Transition appear show={show} as={React.Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onCloseModal}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-90" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center text-center">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                {/* Dialog content */}
                <Dialog.Panel className="relative flex h-screen w-screen flex-col items-center justify-center overflow-hidden bg-transparent">
                  {/* Close right button */}
                  {isOpening ? (
                    <video autoPlay playsInline onEnded={onOpeningVideoEnded} controls={false}>
                      <source src="/videos/LootBoxAnimation_WEBM.webm" type="video/webm; codecs=vp9" />
                      <source src="/videos/LootBoxAnimation_MOV.mp4" type="video/mp4; codecs=hvc1" />
                      <source src="/videos/LootBoxAnimation_MP4.mp4" type="video/mp4" />
                    </video>
                  ) : (
                    <div className="relative flex h-[650px] w-full items-center justify-center sm:w-[700px]">
                      <div className="absolute right-7 top-3 cursor-pointer" onClick={onCloseModal}>
                        <ModalCloseIcon />
                      </div>
                      <div className="flex h-auto w-[567px] flex-col items-center justify-center">
                        {/* Crate name */}
                        <span className="mb-[13px] font-tactic-extended text-3xl font-bold leading-8 tracking-[0.025em] text-white">
                          {product.name.toUpperCase()}
                        </span>
                        {/* Crate category */}
                        <span className="mb-[36px] font-tactic-extended text-xl font-bold leading-[24px] tracking-[0em] text-[#8C8C8C]">
                          {product.category.toUpperCase()}
                        </span>
                        {/* Crate rarity */}
                        <span
                          className="mb-[45px] font-tactic-extended text-input font-normal leading-[17px] tracking-[0em]"
                          style={{
                            color: PaidProductRarityColor[product.rarity].text,
                          }}
                        >
                          {product.rarity.toUpperCase()}
                        </span>
                        {/* Crate image */}
                        <Transition appear={isCrateOpened} as={React.Fragment}>
                          <Transition.Child
                            className="mx-auto max-w-md"
                            enter="transition-all ease-in-out duration-1000 delay-[300ms]"
                            enterFrom="opacity-0 -translate-y-20"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition-all ease-in-out duration-300"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <div
                              className="relative mb-16 h-[300px] w-[300px] p-1"
                              style={{
                                backgroundImage: `linear-gradient(180deg, ${
                                  PaidProductRarityColor[product.rarity].text
                                } 0%, transparent 100%)`,
                                backgroundSize: '100% 100%',
                                boxShadow: `58px 0px 100px -60px ${
                                  PaidProductRarityColor[product.rarity].shadow
                                }, -58px 0px 100px -60px ${PaidProductRarityColor[product.rarity].shadow}`,
                              }}
                            >
                              <div className="relative h-[calc(300px-8px)] w-[calc(300px-8px)]">
                                {isDayZeroPatch ? (
                                  <>
                                    {!isIOS && (isChrome || isEdge || isFirefox || isOpera) ? (
                                      <video
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        className="h-full w-full object-contain object-center"
                                      >
                                        <source src="/images/profile/clawnft_animation10.webm" type="video/webm" />
                                      </video>
                                    ) : (
                                      <Image
                                        src={product.image}
                                        layout="fill"
                                        objectFit="cover"
                                        alt="Claws NFT Animation"
                                      />
                                    )}
                                  </>
                                ) : (
                                  <Image
                                    src={imageError ? '/images/logo-yellow.png' : product.image}
                                    layout="fill"
                                    objectFit={'cover'}
                                    alt={productName}
                                    placeholder="blur"
                                    blurDataURL={founderBlurDataURL}
                                    onError={(event) => setImageError(true)}
                                    onEmptied={() => setImageError(true)}
                                  />
                                )}
                              </div>
                            </div>
                          </Transition.Child>
                        </Transition>

                        {isDkCrate && (
                          <div className="pb-10">
                            <div className="mb-4 flex justify-center">
                              <Dropdown
                                label=""
                                items={quantityOptions}
                                defaultValue={quantity}
                                onSelect={handleSelectQuantity}
                              />
                            </div>
                            <div className="flex flex-col items-center sm:flex-row">
                              <Input
                                rtlDirection
                                {...register('email', { required: true })}
                                type="email"
                                label=""
                                error={errors.email?.message}
                                placeholder="Recipient Email"
                              />
                              <div className="relative flex h-[60px] w-[60px] items-center justify-center">
                                {isVerifyUserEmailLoading ? (
                                  <LoadingSpinner isShown isFullScreen={false} />
                                ) : verifyUserEmailStatus === 'success' ? (
                                  <span className="text-[40px]">✅</span>
                                ) : verifyUserEmailStatus === 'error' ? (
                                  <span className="text-[40px]">❌</span>
                                ) : (
                                  <></>
                                )}
                              </div>
                              <Button
                                onClick={handleTransferClick}
                                state={verifyUserEmailStatus !== 'success' || quantity === 'Quantity' ? 'disabled' : ''}
                                disabled={verifyUserEmailStatus !== 'success' || quantity === 'Quantity'}
                              >
                                <span>Transfer</span>
                              </Button>

                              <TransferConfirmModal
                                isOpen={isTransferConfirmModalOpen}
                                onClose={handleCloseTransferConfirmModal}
                                email={formValues.email ?? ''}
                                crateCount={quantity === 'Quantity' ? 0 : parseInt(quantity)}
                                receiverUserId={receiverUserId}
                                onTransferSuccess={handleTransferSuccess}
                              />
                            </div>
                          </div>
                        )}

                        {/* Action button */}
                        <div className="flex flex-col-reverse md:flex-row md:space-x-4 md:space-y-0">
                          <Button
                            className="w-[275px] border-none outline-none md:!w-[250px] lg:!w-[246px]"
                            variant="secondary"
                            onClick={onCloseModal}
                          >
                            <span>Back</span>
                          </Button>
                          {isDkCrate && !isCrateOpened && (
                            <Button
                              className="mb-7 w-[275px] outline-none md:mb-0 md:!w-[250px] lg:!w-[246px]"
                              onClick={onClickOpenCrate}
                            >
                              <span>Open</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default InventoryItemOpenModal
